import { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'reduxTyped'

import { useViewPayload } from 'apps/aiAutomation/hooks/layout'
import { AiAutomationAnalytics } from 'apps/aiAutomation/lib/analytics'
import { SidebarViewType } from 'apps/aiAutomation/lib/sidebar'
import {
  AiAutomationKnowledgeActions,
  AiAutomationKnowledgeSelectors,
} from 'apps/aiAutomation/store/knowledge'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import {
  KnowledgeEntry,
  KnowledgeEntryDraft,
} from 'shared/api/requests/aiBusinessKnowledge/schemas'

export const useEditor = () => {
  const dispatch = useAppDispatch()

  const entryId = useViewPayload(SidebarViewType.KNOWLEDGE_EDITOR)?.entryId ?? null

  const entries = useAppSelector(AiAutomationKnowledgeSelectors.getList)
  const selectedEntry = useAppSelector((state) =>
    entryId ? AiAutomationKnowledgeSelectors.getItemById(state, entryId) : null,
  )
  const isLoading = useAppSelector((state) =>
    AiAutomationKnowledgeSelectors.getIsItemLoading(state, entryId),
  )

  const save = useCallback(
    async (entry: KnowledgeEntry | KnowledgeEntryDraft) => {
      if (selectedEntry) {
        await dispatch(AiAutomationKnowledgeActions.editEntryFx(entry as KnowledgeEntry)).unwrap()

        AiAutomationAnalytics.logKnowledgeEntryUpdated({ id: entry.id })
      } else {
        await dispatch(
          AiAutomationKnowledgeActions.addEntryFx(entry as KnowledgeEntryDraft),
        ).unwrap()

        AiAutomationAnalytics.logKnowledgeEntryCreated({ id: entry.id })
      }

      dispatch(AiAutomationLayoutActions.showConfirmChanged(false))

      if (entries.length === 0) {
        dispatch(
          AiAutomationLayoutActions.currentViewReplaced({
            view: SidebarViewType.KNOWLEDGE_ADDED,
          }),
        )
      } else {
        dispatch(AiAutomationLayoutActions.sidebarClosed())
      }
    },
    [selectedEntry, dispatch, entries.length],
  )

  useEffect(() => {
    if (entryId) {
      dispatch(AiAutomationKnowledgeActions.ensureEntryLoadedFx(entryId))
    }
  }, [dispatch, entryId])

  return {
    selectedEntry,
    isLoading,
    save,
  }
}
